import type {
  AlertPrefs, AppConnection, ImportCapabilities, ImportSlot, ParsedRoster, YahooLeague,
} from './types';
import type {
  CounterResult, FantasyAlert, FantasyLeague, FantasyMeta, FantasyPlayerDetail, FantasyTeam, LineupView,
  PlayerSearchResult, ProjectionsTable, RiskMode, SleeperLeagueList, TradeEvaluation, TradeHistoryRow,
} from './types';
import type {
  BacktestResult, BetRow, CreatedBet, EmailPrefs, Briefing, GameDetail, Health, InjuryReport, LedgerSummary,
  AdminHealth, AdminJobRun, ModelConfig, NewsFeed, Overview, PicksBoard, PlayerPropDetail, PropsBoard, RosterResponse, SettingsResponse, Team,
  TrackRecord, UnifiedFeed, MarketsBoard, WatchItem, AdminQb, QbChangeResult, TfMarket, TfRange, TfSide, TradingFloorView,
} from './types';

/** Fired when the API rejects a call because the session is gone. */
export const UNAUTHORIZED_EVENT = 'deltapicks:unauthorized';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly detail?: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    // The session lives in a cookie, so every call has to carry it.
    credentials: 'same-origin',
    // A FormData upload sets its own multipart content type.
    headers: init?.body instanceof FormData ? { ...(init?.headers ?? {}) } : { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let detail: string | undefined;
    try {
      const body = await res.json();
      message = body.error ?? message;
      detail = body.detail;
    } catch {
      // A non-JSON body means the response did not come from the API at all --
      // in dev that is the Vite proxy answering because the backend is down.
      if (res.status === 500) message = 'API server is not reachable';
    }
    // A session can lapse while the tab sits open. Announce it once, centrally,
    // so the app returns to the login screen instead of every panel erroring.
    if (res.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }

    throw new ApiError(message, res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type AuthUser = { id: number; email: string; emailVerified: boolean; isAdmin: boolean };

export type AdminUser = {
  id: number;
  email: string;
  createdAt: string;
  verifiedAt: string | null;
  lastSeen: string | null;
  leagues: number;
  bets: number;
};

export type AdminEmail = {
  id: number;
  to_email: string;
  kind: 'welcome' | 'reset' | 'signup_alert' | 'ops_alert';
  status: 'sent' | 'logged' | 'failed';
  error: string | null;
  created_at: string;
};

export type AdminOdds = {
  enabled: boolean;
  quota: { remaining: number | null; used: number | null; at: string | null };
  month: { spent: number; projected: number | null; byJob: Array<{ job: string; calls: number; credits: number }> };
  unmatched: Array<{
    name: string;
    game: string | null;
    firstSeen: string;
    suggestions: Array<{ playerId: string; name: string; team: string; position: string }>;
  }>;
};

export const api = {
  health: () => request<Health>('/health'),

  markets: (week?: number, seasonType?: number) => {
    const qs = new URLSearchParams();
    if (week != null) qs.set('week', String(week));
    if (seasonType != null) qs.set('seasonType', String(seasonType));
    return request<MarketsBoard>(`/markets${qs.size ? `?${qs}` : ''}`);
  },
  tradingFloor: (gameId: string, q: { market: TfMarket; side?: TfSide; range: TfRange; books: boolean }) => {
    const qs = new URLSearchParams({ market: q.market, range: q.range });
    if (q.side) qs.set('side', q.side);
    if (q.books) qs.set('books', '1');
    return request<TradingFloorView>(`/trading-floor/${encodeURIComponent(gameId)}?${qs}`);
  },

  watchlist: () => request<{ items: WatchItem[] }>('/watchlist'),
  push: {
    key: () => request<{ publicKey: string; subscribed: boolean }>('/push/key'),
    subscribe: (sub: PushSubscriptionJSON) => request<{ ok: true }>('/push/subscribe', { method: 'POST', body: JSON.stringify(sub) }),
    unsubscribe: (endpoint: string) => request<{ ok: true }>('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  },
  watch: (item: { kind: 'game' | 'prop'; ref: string; gameId?: string | null; label: string }) =>
    request<{ ok: true }>('/watchlist', { method: 'POST', body: JSON.stringify(item) }),
  unwatch: (kind: 'game' | 'prop', ref: string) =>
    request<void>(`/watchlist/${kind}/${encodeURIComponent(ref)}`, { method: 'DELETE' }),

  me: () => request<{ user: AuthUser }>('/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  signup: (email: string, password: string) =>
    request<{ user: AuthUser }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  verifyEmail: (token: string) =>
    request<{ ok: boolean; user: AuthUser | null }>('/auth/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  resendVerification: () => request<{ ok: boolean }>('/auth/verify/resend', { method: 'POST' }),
  forgotPassword: (email: string) =>
    request<{ ok: boolean }>('/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) =>
    request<{ user: AuthUser }>('/auth/reset', { method: 'POST', body: JSON.stringify({ token, password }) }),

  admin: {
    users: () =>
      request<{
        users: AdminUser[];
        totals: { users: number; verified: number; lastWeek: number };
        email: { provider: 'resend' | 'log'; from: string; appUrl: string };
      }>('/admin/users'),
    emails: () => request<{ emails: AdminEmail[] }>('/admin/emails'),
    odds: () => request<AdminOdds>('/admin/odds'),
    fixAlias: (name: string, playerId: string) =>
      request<{ ok: true }>('/admin/odds/alias', { method: 'POST', body: JSON.stringify({ name, playerId }) }),
    qb: () => request<AdminQb>('/admin/qb'),
    setStarter: (team: string, qbId: string | null, note?: string) =>
      request<QbChangeResult>('/admin/qb/starter', { method: 'POST', body: JSON.stringify({ team, qbId, note: note || undefined }) }),
    setDepth: (team: string, order: string[] | null, note?: string) =>
      request<QbChangeResult>('/admin/qb/depth', {
        method: 'POST',
        body: JSON.stringify(order ? { team, order, note: note || undefined } : { team, reset: true }),
      }),
    resolveFlag: (id: number, status: 'dismissed' | 'confirmed') =>
      request<{ ok: true }>(`/admin/qb/flags/${id}`, { method: 'POST', body: JSON.stringify({ status }) }),
    health: () => request<AdminHealth>('/admin/health'),
    runJob: (name: string) =>
      request<{ ok: boolean; run: AdminJobRun }>(`/admin/jobs/${encodeURIComponent(name)}/run`, { method: 'POST' }),
  },

  picks: (week?: number, seasonType?: number) => {
    const qs = new URLSearchParams();
    if (week) qs.set('week', String(week));
    // 2 = regular season, 3 = playoffs. Omitted means "whatever week it is now".
    if (seasonType) qs.set('seasonType', String(seasonType));
    return request<PicksBoard>(`/picks${qs.size ? `?${qs}` : ''}`);
  },

  game: (gameId: string, week?: number, seasonType?: number) => {
    const qs = new URLSearchParams();
    if (week) qs.set('week', String(week));
    if (seasonType) qs.set('seasonType', String(seasonType));
    return request<GameDetail>(`/picks/${gameId}${qs.size ? `?${qs}` : ''}`);
  },

  setRoof: (gameId: string, closed: boolean) =>
    request<{ gameId: string; closed: boolean }>(`/picks/${gameId}/roof`, {
      method: 'POST',
      body: JSON.stringify({ closed }),
    }),

  feed: (params: { type?: string; gameId?: string; team?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.type && params.type !== 'all') qs.set('type', params.type);
    if (params.gameId) qs.set('gameId', params.gameId);
    if (params.team) qs.set('team', params.team);
    if (params.limit) qs.set('limit', String(params.limit));
    return request<UnifiedFeed>(`/feed${qs.size ? `?${qs}` : ''}`);
  },

  props: (week?: number, seasonType?: number) => {
    const qs = new URLSearchParams();
    if (week) qs.set('week', String(week));
    if (seasonType) qs.set('seasonType', String(seasonType));
    return request<PropsBoard>(`/props${qs.size ? `?${qs}` : ''}`);
  },
  playerProps: (athleteId: string, week?: number, seasonType?: number) => {
    const qs = new URLSearchParams();
    if (week) qs.set('week', String(week));
    if (seasonType) qs.set('seasonType', String(seasonType));
    return request<PlayerPropDetail>(`/props/player/${athleteId}${qs.size ? `?${qs}` : ''}`);
  },

  injuries: () => request<InjuryReport>('/injuries'),

  teams: () => request<{ teams: Team[]; count: number }>('/rosters'),
  roster: (teamId: string) => request<RosterResponse>(`/rosters/${teamId}`),

  news: (params: { team?: string; tag?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.team) qs.set('team', params.team);
    if (params.tag) qs.set('tag', params.tag);
    if (params.limit) qs.set('limit', String(params.limit));
    return request<NewsFeed>(`/news${qs.size ? `?${qs}` : ''}`);
  },
  briefing: (team?: string) =>
    request<Briefing>('/news/briefing', { method: 'POST', body: JSON.stringify({ team }) }),

  overview: () => request<Overview>('/overview'),

  modelConfig: () => request<ModelConfig>('/model/config'),
  trackRecord: () => request<TrackRecord>('/model/track-record'),
  /** Runs against the locked config; the server ignores any settings sent. */
  backtest: (body: Record<string, unknown>) =>
    request<BacktestResult>('/model/backtest', { method: 'POST', body: JSON.stringify(body) }),

  bets: (params: { status?: string; week?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.week != null) qs.set('week', String(params.week));
    return request<{ bets: BetRow[]; summary: LedgerSummary; bankroll: SettingsResponse['bankroll'] }>(
      `/bets${qs.size ? `?${qs}` : ''}`,
    );
  },
  createBet: (body: Record<string, unknown>) =>
    request<CreatedBet>('/bets', { method: 'POST', body: JSON.stringify(body) }),

  alerts: {
    prefs: () => request<{ prefs: EmailPrefs; email: { configured: boolean; mailingAddress: boolean } }>('/alerts/prefs'),
    save: (patch: Partial<EmailPrefs>) =>
      request<{ prefs: EmailPrefs }>('/alerts/prefs', { method: 'PUT', body: JSON.stringify(patch) }),
    devEmails: () => request<{ emails: Array<{ name: string; subject: string; category: string }> }>('/alerts/dev/emails'),
  },
  updateBet: (id: number, body: Record<string, unknown>) =>
    request<BetRow>(`/bets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteBet: (id: number) => request<void>(`/bets/${id}`, { method: 'DELETE' }),
  gradeBets: () =>
    request<{ graded: number; skipped: number; errors: string[]; summary: LedgerSummary }>('/bets/grade', {
      method: 'POST',
    }),

  settings: () => request<SettingsResponse>('/settings'),
  // No saveModel: the model is locked in code and has no write endpoint.
  saveBankroll: (body: Record<string, unknown>) =>
    request<SettingsResponse['bankroll']>('/settings/bankroll', { method: 'PUT', body: JSON.stringify(body) }),
  saveDisplay: (body: Record<string, unknown>) =>
    request<SettingsResponse['display']>('/settings/display', { method: 'PUT', body: JSON.stringify(body) }),
  resetSettings: (scope: string) =>
    request<Record<string, unknown>>('/settings/reset', { method: 'POST', body: JSON.stringify({ scope }) }),
  refresh: () => request<{ ok: boolean }>('/settings/refresh', { method: 'POST' }),

  fantasy: {
    meta: () => request<FantasyMeta>('/fantasy/meta'),
    leagues: () => request<{ leagues: FantasyLeague[] }>('/fantasy/leagues'),
    league: (id: number) => request<{ league: FantasyLeague; teams: FantasyTeam[] }>(`/fantasy/leagues/${id}`),
    createLeague: (body: { name: string; preset?: string; slots?: string[]; teamCount?: number }) =>
      request<FantasyLeague>('/fantasy/leagues', { method: 'POST', body: JSON.stringify(body) }),
    updateLeague: (id: number, body: Record<string, unknown>) =>
      request<FantasyLeague>(`/fantasy/leagues/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteLeague: (id: number) => request<void>(`/fantasy/leagues/${id}`, { method: 'DELETE' }),
    sync: (id: number) =>
      request<{ unmatched: string[]; league: FantasyLeague }>(`/fantasy/leagues/${id}/sync`, { method: 'POST' }),

    addPlayer: (id: number, playerId: string, teamId?: number) =>
      request<{ teams: FantasyTeam[] }>(`/fantasy/leagues/${id}/roster`, {
        method: 'POST',
        body: JSON.stringify({ playerId, teamId }),
      }),
    bulkAdd: (id: number, lines: string[], teamId?: number) =>
      request<{ added: string[]; unmatched: string[]; teams: FantasyTeam[] }>(`/fantasy/leagues/${id}/roster/bulk`, {
        method: 'POST',
        body: JSON.stringify({ lines, teamId }),
      }),
    removePlayer: (id: number, playerId: string, teamId?: number) =>
      request<{ teams: FantasyTeam[] }>(
        `/fantasy/leagues/${id}/roster/${encodeURIComponent(playerId)}${teamId ? `?teamId=${teamId}` : ''}`,
        { method: 'DELETE' },
      ),
    addTeam: (id: number, name: string) =>
      request<{ teams: FantasyTeam[] }>(`/fantasy/leagues/${id}/teams`, { method: 'POST', body: JSON.stringify({ name }) }),
    deleteTeam: (id: number, teamId: number) =>
      request<{ teams: FantasyTeam[] }>(`/fantasy/leagues/${id}/teams/${teamId}`, { method: 'DELETE' }),

    lineup: (id: number, week?: number, risk?: RiskMode) => {
      const qs = new URLSearchParams();
      if (week) qs.set('week', String(week));
      if (risk) qs.set('risk', risk);
      return request<LineupView>(`/fantasy/leagues/${id}/lineup${qs.size ? `?${qs}` : ''}`);
    },
    projections: (id: number, week?: number, position?: string) => {
      const qs = new URLSearchParams();
      if (week) qs.set('week', String(week));
      if (position && position !== 'all') qs.set('position', position);
      return request<ProjectionsTable>(`/fantasy/leagues/${id}/projections${qs.size ? `?${qs}` : ''}`);
    },
    player: (id: number, playerId: string) =>
      request<FantasyPlayerDetail>(`/fantasy/leagues/${id}/player/${encodeURIComponent(playerId)}`),

    alerts: (leagueId?: number) =>
      request<{ alerts: FantasyAlert[]; unread: number }>(`/fantasy/alerts${leagueId ? `?leagueId=${leagueId}` : ''}`),
    readAlerts: (ids?: number[]) =>
      request<{ updated: number }>('/fantasy/alerts/read', { method: 'POST', body: JSON.stringify({ ids }) }),

    trade: (id: number, body: { partnerTeamId: number | null; give: string[]; get: string[] }) =>
      request<TradeEvaluation>(`/fantasy/leagues/${id}/trade`, { method: 'POST', body: JSON.stringify(body) }),
    counter: (id: number, body: { partnerTeamId: number | null; give: string[]; get: string[] }) =>
      request<CounterResult>(`/fantasy/leagues/${id}/trade/counter`, { method: 'POST', body: JSON.stringify(body) }),
    trades: (id: number) => request<{ trades: TradeHistoryRow[] }>(`/fantasy/leagues/${id}/trades`),

    search: (q: string, position?: string) => {
      const qs = new URLSearchParams({ q });
      if (position && position !== 'all') qs.set('position', position);
      return request<{ players: PlayerSearchResult[] }>(`/fantasy/players?${qs}`);
    },

    importCapabilities: () => request<ImportCapabilities>('/fantasy/import/capabilities'),
    /** Screenshots and files go up as multipart; pasted text as JSON. */
    parseRoster: (input: { files?: File[]; text?: string }) => {
      if (input.files?.length) {
        const form = new FormData();
        input.files.forEach((f) => form.append('files', f));
        return request<ParsedRoster>('/fantasy/parse-roster', { method: 'POST', body: form });
      }
      return request<ParsedRoster>('/fantasy/parse-roster', { method: 'POST', body: JSON.stringify({ text: input.text }) });
    },
    saveRoster: (body: {
      importId?: number;
      leagueId?: number;
      newLeague?: { name: string; preset?: string };
      rivalName?: string;
      players: Array<{ playerId: string; slot: ImportSlot }>;
    }) => request<{ league: FantasyLeague; teams: FantasyTeam[] }>('/fantasy/roster/save', { method: 'POST', body: JSON.stringify(body) }),
    setLineup: (id: number, changes: Array<{ playerId: string; slot: string }>) =>
      request<LineupView>(`/fantasy/leagues/${id}/lineup`, { method: 'PATCH', body: JSON.stringify({ changes }) }),
    disconnect: (id: number) => request<{ league: FantasyLeague }>(`/fantasy/leagues/${id}/disconnect`, { method: 'POST' }),
    alertPrefs: (id: number) => request<AlertPrefs>(`/fantasy/leagues/${id}/alert-prefs`),
    saveAlertPrefs: (id: number, prefs: Partial<AlertPrefs>) =>
      request<AlertPrefs>(`/fantasy/leagues/${id}/alert-prefs`, { method: 'PUT', body: JSON.stringify(prefs) }),
    dismissAlert: (alertId: number) => request<{ ok: true }>(`/fantasy/lineup-alerts/${alertId}/dismiss`, { method: 'POST' }),
    swapAlert: (alertId: number) => request<LineupView>(`/fantasy/lineup-alerts/${alertId}/swap`, { method: 'POST' }),
    connections: () => request<{ connections: AppConnection[]; yahooAvailable: boolean }>('/fantasy/connections'),
    removeConnection: (app: 'sleeper' | 'yahoo') => request<void>(`/fantasy/connections/${app}`, { method: 'DELETE' }),
    yahooConnect: () => request<{ url: string }>('/fantasy/yahoo/connect'),
    yahooLeagues: () => request<{ leagues: YahooLeague[] }>('/fantasy/yahoo/leagues'),
    yahooImport: (leagueKey: string) =>
      request<{ league: FantasyLeague; unmatched: string[] }>('/fantasy/yahoo/import', { method: 'POST', body: JSON.stringify({ leagueKey }) }),

    sleeperLeagues: (username: string) =>
      request<SleeperLeagueList>(`/fantasy/sleeper/leagues?username=${encodeURIComponent(username)}`),
    sleeperImport: (username: string, leagueId: string) =>
      request<{ league: FantasyLeague; unmatched: string[] }>('/fantasy/sleeper/import', {
        method: 'POST',
        body: JSON.stringify({ username, leagueId }),
      }),
  },
};
